import { Button, ButtonStyle } from '@atlas/components';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { providersRequest } from 'api/casino';
import { ErrorEmptyState, ProvidersEmptyState } from 'components/EmptyStates';
import Loader from 'components/Loader';
import ProviderCard from 'components/ProviderCard';
import useLayout from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useSliceRequest from 'utils/useSliceRequest';

import styles from './ProvidersPage.module.scss';

type ProvidersProps = {
  sortDirection?: 'asc' | 'desc';
  search?: string;
};

const Providers = ({ sortDirection, search }: ProvidersProps) => {
  const layout = useLayout();

  const requestProps = useMemo(
    () => ({
      query: {
        ...(sortDirection && {
          sortDirection,
          sortField: 'displayedName',
        }),
        ...(search && {
          search,
          searchField: 'displayedName',
        }),
      },
      slice: {
        itemsPerSlice: 28,
      },
    }),
    [sortDirection, search],
  );

  const { loading, data, hasNext, error, loadMore } = useSliceRequest(
    providersRequest,
    requestProps,
  );

  const isLoading = loading === LoadingState.loading;
  const isEmpty = loading === LoadingState.success && !data.length;
  const isShowMoreVisible = hasNext && loading === LoadingState.success;

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  if (error) {
    return <ErrorEmptyState />;
  }

  return (
    <div className={classNames(styles.providers, styles[layout])}>
      {isEmpty ? (
        <ProvidersEmptyState />
      ) : (
        <div className={styles['providers-list']}>
          {data.map((provider) => (
            <ProviderCard
              key={provider.id}
              iconUrl={provider.iconUrl}
              displayedName={provider.displayedName}
            />
          ))}
        </div>
      )}
      {isLoading && <Loader />}
      {isShowMoreVisible && (
        <Button
          className={styles['show-more']}
          onClick={loadMore}
          style={ButtonStyle.outline}
        >
          <Translation message="show_more" />
        </Button>
      )}
    </div>
  );
};

export default Providers;
